<script setup lang="ts">
import { computed } from 'vue';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

const props = withDefaults(
  defineProps<{
    variant?: Variant;
    size?: Size;
    loading?: boolean;
    disabled?: boolean;
    block?: boolean;
    type?: 'button' | 'submit';
  }>(),
  { variant: 'primary', size: 'md', loading: false, disabled: false, block: false, type: 'button' },
);

const isInert = computed(() => props.disabled || props.loading);
</script>

<template>
  <button
    :type="props.type"
    :class="['btn', `btn--${props.variant}`, `btn--${props.size}`, { 'btn--block': props.block }]"
    :disabled="isInert"
    :aria-busy="props.loading"
  >
    <span v-if="props.loading" class="btn__spinner" aria-hidden="true" />
    <span :class="{ 'btn__label--hidden': props.loading }" class="btn__label">
      <slot />
    </span>
  </button>
</template>

<style scoped>
.btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  font-weight: var(--weight-semibold);
  cursor: pointer;
  white-space: nowrap;
  transition:
    transform var(--duration-fast) var(--ease-spring),
    box-shadow var(--duration-normal) var(--ease-out),
    background-color var(--duration-fast) var(--ease-out),
    border-color var(--duration-fast) var(--ease-out);
}

.btn:not(:disabled):hover {
  transform: translateY(-1px);
}

.btn:not(:disabled):active {
  transform: translateY(0);
}

.btn:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.btn--sm {
  padding: var(--space-2) var(--space-3);
  font-size: var(--text-sm);
}
.btn--md {
  padding: var(--space-3) var(--space-5);
  font-size: var(--text-sm);
}
.btn--lg {
  padding: var(--space-4) var(--space-6);
  font-size: var(--text-base);
}
.btn--block {
  width: 100%;
}

.btn--primary {
  background: var(--gradient-brand);
  color: var(--text-on-brand);
  box-shadow: var(--shadow-sm);
}
.btn--primary:not(:disabled):hover {
  box-shadow: var(--shadow-brand);
}

.btn--secondary {
  background: var(--surface-raised);
  color: var(--text-primary);
  border-color: var(--border-subtle);
  box-shadow: var(--shadow-xs);
}
.btn--secondary:not(:disabled):hover {
  border-color: var(--border-strong);
}

.btn--ghost {
  background: transparent;
  color: var(--text-secondary);
}
.btn--ghost:not(:disabled):hover {
  background: var(--surface-sunken);
  color: var(--text-primary);
}

.btn--danger {
  background: var(--danger-500);
  color: #fff;
}
.btn--danger:not(:disabled):hover {
  background: var(--danger-600);
}

.btn__label--hidden {
  opacity: 0;
}

.btn__spinner {
  position: absolute;
  width: 1em;
  height: 1em;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: var(--radius-full);
  animation: btn-spin 0.6s linear infinite;
}

@keyframes btn-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .btn,
  .btn:hover {
    transform: none;
  }
  .btn__spinner {
    animation-duration: 1.5s;
  }
}
</style>
